import { ArrowBigRight, Car, Forklift, GitFork } from "lucide-react";
import Button from "@/common/components/Button";
import { Link } from "react-router-dom";
import { formatUnit } from "@/common/utils/formatters";
import { unitStrategy } from "@/common/constants/unitStrategies.contants";
import { WorkflowScheduleData } from "../types/workflowSchedules.type";
import { maintenanceWorkflowSchedulesKindStrategy } from "../constants/kind.constant";
import { maintenanceWorkflowSchedulesExecutionOptions } from "../constants/executionOptions.constant";

export class MaintenanceWorkflowSchedulesTransformer {
  static tableTransformer() {
    return (workflowSchedules: WorkflowScheduleData[]) => {
      return workflowSchedules.map((workflowSchedule) => {
        return {
          ...workflowSchedule,
          kind: maintenanceWorkflowSchedulesKindStrategy[workflowSchedule.kind as keyof typeof maintenanceWorkflowSchedulesKindStrategy],
          action: (
            <Link to={workflowSchedule.id}>
              <Button name="access_workflow_schedule" variant='link-white' className="w-full" operationKlassToCheckPermission="Client::Maintenance::WorkflowSchedules::Show">
                <ArrowBigRight />
              </Button>
            </Link>
          )
        };
      });
    };
  }

  static formDataTransformer(workflowSchedule?: WorkflowScheduleData) {
    if (!workflowSchedule) return {};

    const hasKilometers = !!workflowSchedule.interval_in_kilometers_usage
    const hasDays = !!workflowSchedule.interval_in_days
    const hasHours = !!workflowSchedule.interval_in_hours_usage

    const executionsMap: Record<string, boolean> = {
      kilometers_usage: hasKilometers,
      days: hasDays,
      hours_usage: hasHours,
    }

    const executionOptions = maintenanceWorkflowSchedulesExecutionOptions.filter(
      (option) => executionsMap[option.value]
    )

    return {
      display_name: workflowSchedule?.display_name || undefined,
      observations: workflowSchedule.observations || undefined,

      kind: {
        label: workflowSchedule.kind 
          ? maintenanceWorkflowSchedulesKindStrategy[workflowSchedule.kind as keyof typeof maintenanceWorkflowSchedulesKindStrategy] || workflowSchedule.kind 
          : undefined,
        value: workflowSchedule?.kind || undefined
      },

      equipment_category_ids: {
        value: workflowSchedule.workflow_schedule_equipment_categories?.[0]?.equipment_category?.id,
        label: workflowSchedule.workflow_schedule_equipment_categories?.[0]?.equipment_category?.display_name,
        icon: <Forklift strokeWidth={1} size={20} />
      },

      branch_ids: workflowSchedule?.workflow_schedule_branches?.map((item: any) => ({
        value: item.branch.id,
        label: item.branch.display_name,
        icon: <GitFork strokeWidth={1} size={20} />
      })),

      manufacturer_ids: workflowSchedule?.workflow_schedule_manufacturers?.map((item: any) => ({
        value: item.manufacturer.id,
        label: item.manufacturer.display_name,
        icon: <Car strokeWidth={1} size={20} />
      })),

      equipment_model_ids: workflowSchedule?.workflow_schedule_equipment_models?.map((item: any) => ({
        value: item.equipment_model.id,
        label: item.equipment_model.display_name,
        icon: <Car strokeWidth={1} size={20} />
      })),

      equipment_ids: workflowSchedule?.workflow_schedule_equipments?.map((item: any) => ({
        value: item.equipment.id,
        label: item.equipment.field_for_display_value,
        icon: <Car strokeWidth={1} size={20} />
      })),

      execution_options: executionOptions || [],

      interval_in_kilometers_usage: workflowSchedule.interval_in_kilometers_usage ? formatUnit(workflowSchedule.interval_in_kilometers_usage, unitStrategy.kilometers) : undefined,
      interval_in_days: workflowSchedule.interval_in_days ? formatUnit(workflowSchedule.interval_in_days, 'Dias') : undefined,
      interval_in_hours_usage: workflowSchedule.interval_in_hours_usage ? formatUnit(workflowSchedule.interval_in_hours_usage, unitStrategy.hours) : undefined,

      objectives_attributes: workflowSchedule.objectives?.map((objective) => ({
        id: objective.id,
        system_id: { label: objective.system.display_name, value: objective.system.id },
        part_id: { label: objective.part.display_name, value: objective.part.id },
        action_id: { label: objective.action.display_name, value: objective.action.id },
      }))
    };
  }
}
